import FileRef from "../classes/FileRef.js";

const JPEG_TYPE = "image/jpeg";
const JPEG_EXTENSION = "jpg";

const MIME_TO_EXT = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/bmp": "bmp",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/avif": "avif",
};

// Derive a file extension from a File/Blob using its name, then MIME type.
export const extFromFile = (file) => {
  const nameExt = FileRef.extension(file);
  if (nameExt) return nameExt;
  const type = String((file && file.type) || "").toLowerCase();
  return MIME_TO_EXT[type] || "";
};

// Derive a file extension from a data URL's MIME type.
export const extFromDataURL = (src) => {
  if (typeof src !== "string") return "";
  const match = src.match(/^data:([^;,]+)[;,]/i);
  if (!match) return "";
  return MIME_TO_EXT[match[1].toLowerCase()] || "";
};

const toJpegName = (name = "image") => {
  const dotIndex = name.lastIndexOf(".");
  const baseName = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  return `${baseName || "image"}.${JPEG_EXTENSION}`;
};

const blobToJpegFile = (blob, originalFile) =>
  new File([blob], toJpegName(originalFile.name), {
    type: JPEG_TYPE,
    lastModified: originalFile.lastModified || Date.now(),
  });

const toBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Unable to convert image"));
        }
      },
      type,
      quality
    );
  });

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };

    image.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };

    image.src = url;
  });

const convertViaNativeImage = async (file) => {
  const image = await loadImageFromFile(file);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  const context = canvas.getContext("2d");
  context.drawImage(image, 0, 0);

  return toBlob(canvas, JPEG_TYPE, 0.92);
};

const convertViaHeic2any = async (file) => {
  const heic2anyModule = await import("heic2any");
  const heic2any = heic2anyModule.default || heic2anyModule;

  const output = await heic2any({
    blob: file,
    toType: JPEG_TYPE,
    quality: 0.92,
  });

  return Array.isArray(output) ? output[0] : output;
};

export const normalizeUploadImageFile = async (file) => {
  if (!file || !FileRef.isHeif(file)) {
    return file;
  }

  let jpegBlob = null;

  try {
    jpegBlob = await convertViaHeic2any(file);
  } catch (error) {
    try {
      jpegBlob = await convertViaNativeImage(file);
    } catch (fallbackError) {
      throw "failedUploadingFiles";
    }
  }

  if (!(jpegBlob instanceof Blob)) {
    throw "failedUploadingFiles";
  }

  return blobToJpegFile(jpegBlob, file);
};
